import { useMemoizedFn } from 'ahooks';
import { clawsGetMyClaws, getMyClawPassStatus } from '@/app/apis';
import { useStore, useStoreApi } from '@/app/providers/store';
import { getClawUserVariant } from '../utils';
import { useFetch } from './use-fetch';

const CLAW_STATUS_POLL_INTERVAL_MS = 20 * 1000;
const CLAW_STATUS_POLL_TIMEOUT_MS = 18 * 60 * 1000;

let clawStatusPollingTimer: ReturnType<typeof setTimeout> | null = null;
let clawStatusPollingStartedAt = 0;
let clawStatusPollingActive = false;

// 仅 CREATING / PENDING（含 UNSPECIFIED 兜底）视为开通中；EXPIRED 是终态，不应再触发轮询或全页 loading
export const isClawProvisioning = (status: string | null | undefined) =>
  status != null && status !== 'CLAW_STATUS_ACTIVE' && status !== 'CLAW_STATUS_EXPIRED';

export const useClaw = () => {
  const storeApi = useStoreApi();
  const { setClawStatus, setActiveClaw, setClawPass, setClawInfoInitialized } = useStore((state) => ({
    setClawStatus: state.setClawStatus,
    setActiveClaw: state.setActiveClaw,
    setClawPass: state.setClawPass,
    setClawInfoInitialized: state.setClawInfoInitialized,
  }));

  const { runAsync: getMyClawsRawAsync } = useFetch(clawsGetMyClaws, {
    manual: true,
    onSuccess: ({ data, error }) => {
      if (error) return;
      if (!data) return;

      const first = data.claws?.[0] ?? null;
      const clawStatus = first?.status ?? null;
      setClawStatus(clawStatus);
      setActiveClaw(first);
      if (first && isClawProvisioning(clawStatus)) {
        startClawStatusPolling();
      } else {
        stopClawStatusPolling();
      }
    },
  });

  const { runAsync: getMyClawPassStatusAsync } = useFetch(getMyClawPassStatus, {
    manual: true,
    onSuccess: ({ data, error }) => {
      if (error) return;
      if (!data) return;
      setClawPass({
        ...data,
        userVariant: getClawUserVariant(data),
      });
    },
  });

  // 与 getMyClaws 并行发起 claw pass 状态请求，统一对外暴露为 getMyClaws / getMyClawsAsync，
  // 这样所有既有调用方（含轮询）都能同时刷新 activeClaw 和 clawPass。
  const getMyClawsAsync = useMemoizedFn(async () => {
    try {
      const [clawsResult] = await Promise.all([getMyClawsRawAsync(), getMyClawPassStatusAsync()]);
      return clawsResult;
    } finally {
      // 以并行请求整体完成作为 claw 初始化完成信号，避免 access 在 clawPass 返回前被误判。
      setClawInfoInitialized(true);
    }
  });

  const getMyClaws = useMemoizedFn(() => {
    getMyClawsAsync();
  });

  const stopClawStatusPolling = () => {
    if (clawStatusPollingTimer) {
      clearTimeout(clawStatusPollingTimer);
      clawStatusPollingTimer = null;
    }
    clawStatusPollingStartedAt = 0;
    clawStatusPollingActive = false;
  };

  const startClawStatusPolling = () => {
    const { clawStatus } = storeApi.getState();
    if (!isClawProvisioning(clawStatus) || clawStatusPollingActive) {
      return;
    }

    clawStatusPollingActive = true;
    clawStatusPollingStartedAt = Date.now();

    const pollStatus = async (): Promise<void> => {
      await getMyClawsAsync();

      const { clawStatus: latestClawStatus } = storeApi.getState();
      const isTimeout = Date.now() - clawStatusPollingStartedAt >= CLAW_STATUS_POLL_TIMEOUT_MS;
      if (!isClawProvisioning(latestClawStatus) || isTimeout) {
        stopClawStatusPolling();
        return;
      }

      clawStatusPollingTimer = setTimeout(() => {
        pollStatus();
      }, CLAW_STATUS_POLL_INTERVAL_MS);
    };

    clawStatusPollingTimer = setTimeout(() => {
      pollStatus();
    }, CLAW_STATUS_POLL_INTERVAL_MS);
  };

  return {
    getMyClaws,
    getMyClawsAsync,
    startClawStatusPolling,
    stopClawStatusPolling,
  };
};
